import { Component, OnInit, ViewEncapsulation } from '@angular/core'
import { FormBuilder } from '@angular/forms'
import { Router } from '@angular/router'
import {
  GlobalMessageService,
  GlobalMessageType,
  CmsService,
  TranslationService,
} from '@spartacus/core'
import { LaunchDialogService } from '@spartacus/storefront'
import { DialogResponse } from '../../config/dialog.config'
import { CommonService } from '../../../shared/services/common.service'
import { BreadcrumbService } from '../../../shared/components/breadcrumb/breadcrumb.service'
import { PRE_URL } from '../../../shared/constants'

export enum Roles {
  Admin = 'administrator',
  Buyer = 'buyer',
  Viewer = 'viewer',
}

export interface Element {
  shortName: string
  name: string
  uid: string
  firstName: string
  lastName: string
  email: string
  roles: string[]
  status: string
  role: string
  active: boolean
  requestedForRoleChange: string
}

@Component({
  standalone: false,
  selector: 'vs-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class UserManagementComponent implements OnInit {
  public editClick
  public hideContent: boolean

  displayedColumns = ['name', 'uid', 'email', 'roles', 'status', 'action']
  dataSource = []

  currentUser
  users: Element[] = []

  showSuccessMessage = false
  isDisablingUser = false
  userFlag = true
  registrationLink = ''
  displayRoleMatrix = false

  userRoleList: any
  userRoleStatus: any
  selectedRoleCode: any

  constructor(
    public fb: FormBuilder,
    private commonService: CommonService,
    public router: Router,
    private breadCrumbService: BreadcrumbService,
    private globalMessageService: GlobalMessageService,
    private translate: TranslationService,
    private cmsService: CmsService
  ) {
    this.hideContent = false
  }

  ngOnInit(): void {
    this.breadCrumbService.setBreadCrumbs([])
    this.translate
      .translate('userManagement.manageUsers')
      .subscribe((res: string) =>
        this.breadCrumbService.setBreadcrumbTitle(res)
      )

    this.commonService.getUserData().subscribe(
      (user) => {
        if (user) {
          this.currentUser = user
        }
        this.loadUsers()
      },
      (err) => {}
    )
    this.cmsService
      .getComponentData('RegistrationLink')
      .subscribe((res: any) => {
        this.registrationLink = res.url
      })
  }

  loadUsers() {
    this.showSuccessMessage = false
    this.isDisablingUser = false
    this.commonService
      .getData_WithoutRetry(
        `${PRE_URL}/users/${this.currentUser.uid}/manage-users/users/${this.userFlag}`
      )
      .subscribe(
        (users: any[]) => {
          if (users && users.length) {
            this.dataSource = users.map((user) => {
              try {
                user.shortName =
                  user?.firstName.toUpperCase().charAt(0) +
                  user?.lastName.toUpperCase().charAt(0)
              } catch (e) {
                const userNameParts = user?.name.split(/\s/)
                user.shortName =
                  userNameParts[0]?.toUpperCase().charAt(0) +
                  userNameParts[1]?.toUpperCase().charAt(0)
              }
              user.status = user.active ? 'Active' : 'Disabled'
              this.userRoleStatus = user.status
              this.userRoleList = user.manageUserRoleList
              for (let i = 0; i < user.manageUserRoleList.length; i++) {
                if (user.manageUserRoleList[i].selected) {
                  user.role = user.manageUserRoleList[i].name
                  user.roleDescription = user.manageUserRoleList[i].description
                  break
                }
              }
              return user
            })
          }
        },
        (err) => {
          if (
            err?.error?.errors[0]?.message.toLowerCase() == 'access is denied'
          ) {
            this.router.navigate(['/access-denied'])
          }
        }
      )
  }

  updateUserStatus(uid, status) {
    this.showSuccessMessage = false
    const isActive = status === 'Active'
    this.isDisablingUser = !isActive
    return new Promise((resolve) => {
      this.commonService
        .postData(
          `${PRE_URL}/users/${this.currentUser.uid}/manage-users/${uid}/${isActive}/updateUserStatus`,
          {}
        )
        .subscribe(
          (data: any) => {
            resolve(true)
          },
          (err) => {
            resolve(false)
          }
        )
    })
  }

  updateUserRole(uid, role) {
    this.showSuccessMessage = false
    this.isDisablingUser = false
    let roleString = this.selectedRoleCode

    return new Promise((resolve) => {
      this.commonService
        .postData(
          `${PRE_URL}/users/${this.currentUser.uid}/manage-users/${uid}/${roleString}/saveUserRole`,
          {}
        )
        .subscribe(
          () => {
            resolve(true)
          },
          (err) => {
            resolve(false)
          }
        )
    })
  }

  edit(name) {
    this.editClick = name
    this.hideContent = true
  }

  closeRow(element) {
    this.editClick = ''
    this.hideContent = false
    element.changedRole = null
    element.changedStatus = null

    this.userRoleList = []
    this.userRoleStatus = ''
    this.selectedRoleCode = ''

    this.loadUsers()
  }

  onRoleChange(element, value) {
    element.changedRole = element.code
    this.selectedRoleCode = element.code
  }

  onStatusChange(element, value) {
    if (element.active && value !== 'Active') {
      element.changedStatus = value
    } else if (!element.active && value !== 'Disabled') {
      element.changedStatus = value
    } else {
      element.changedStatus = null
    }
    this.userRoleStatus = element.changedStatus
  }
  navigateToUser(user) {
    this.router.navigate(['manage-users', user.uid])
  }
  toggleSort(toggle) {
    this.userFlag = toggle
    this.loadUsers()
  }

  submitData(element) {
    this.showSuccessMessage = true
    this.isDisablingUser = false
    if (this.selectedRoleCode && this.userRoleStatus) {
      const promise1 = this.updateUserRole(element.uid, this.selectedRoleCode)
      const promise2 = this.updateUserStatus(element.uid, this.userRoleStatus)
      Promise.all([promise1, promise2]).then((response) => {
        if (response[0] && response[1]) {
          this.loadUsers()
          this.showSuccessMessage = true
          element.active = this.userRoleStatus === 'Active'
        } else {
          if (response[0] && !response[1]) {
          } else if (response[1] && !response[0]) {
          } else {
          }
        }
      })
    } else if (this.selectedRoleCode) {
      this.updateUserRole(element.uid, this.selectedRoleCode).then(
        (response) => {
          if (response) {
            // this.showSuccessMessage = true;
            this.loadUsers()
            this.globalMessageService.add(
              { key: 'userManagement.changesSavedSuccessfully' },
              GlobalMessageType.MSG_TYPE_CONFIRMATION,
              9000
            )
            window.scrollTo(0, 0)
          } else {
          }
        }
      )
    } else if (this.userRoleStatus) {
      this.updateUserStatus(element.uid, this.userRoleStatus).then(
        (response) => {
          if (response) {
            // this.showSuccessMessage = true;
            this.showSuccessMessage = false
            this.globalMessageService.add(
              { key: 'userManagement.disabledUsersAccess' },
              GlobalMessageType.MSG_TYPE_INFO,
              9000
            )
            window.scrollTo(0, 0)
            element.active = this.userRoleStatus === 'Active'
          } else {
            this.isDisablingUser = false
          }
        }
      )
    }
    this.editClick = ''
    this.hideContent = false
  }

  redirectToRegister() {
    window.open(this.registrationLink)
  }

  closeRolesMatrixOverlay(event) {
    this.displayRoleMatrix = false
  }

  openRoleMatrix() {
    this.displayRoleMatrix = true
  }
}
